import { useUserContext } from "../../Hooks/useUserContext";
import { useVolumeToMassConversionService } from "../../Hooks/useVolumeToMassConversionService";
import { RouterLink } from "../../Shared/RouterLink";
import { AuthLoadingScreen } from "../../Shared/AuthLoadingScreen";
import { parseRelaxedJson } from "../../Helpers/jsonHelper";
import React, { useState, useEffect } from "react";
import {
	Grid,
	Typography,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Button,
	IconButton,
	Tooltip,
	Paper,
	TextField,
	Snackbar,
	SnackbarContent
} from "@material-ui/core";
import { Spinner } from "../../Shared/Spinner";
import NotLoggedInIcon from "@material-ui/icons/MoodBad";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ImportIcon from "@material-ui/icons/GetApp";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import ConfirmIcon from "@material-ui/icons/CheckCircle";
import green from "@material-ui/core/colors/green";

export function VolumeToMassConversionAdmin(props) {
	const user = useUserContext(props.config);
	const service = useVolumeToMassConversionService(props.config);
	const [items, setItems] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [deletingId, setDeletingId] = useState(null);
	const [importText, setImportText] = useState("");
	const [showImport, setShowImport] = useState(false);
	const [toastOpen, setToastOpen] = useState(false);
	const [toastMessage, setToastMessage] = useState("");

	const normalizeAliasList = (value) => {
		if (!value) return [];
		const values = Array.isArray(value)
			? value
			: value.split(/\||\r?\n/);
		const parts = values.map((part) => (part ?? "").trim()).filter((part) => part);
		const seen = new Set();
		return parts.filter((part) => {
			const key = part.toLowerCase();
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	};

	const formatAliasesForOverview = (value) => normalizeAliasList(value).join("; ");

	const hasPermission = user?.isVolumeToMassAdmin ?? false;

	const sortedItems = [...items].sort((a, b) => {
		const aName = (a?.name ?? "").toString().toLocaleLowerCase();
		const bName = (b?.name ?? "").toString().toLocaleLowerCase();
		if (aName < bName) return -1;
		if (aName > bName) return 1;
		return 0;
	});

	useEffect(() => {
		document.title = "Volume to Mass Conversions - " + props.config.appName;
		if (!hasPermission)
			return;

		loadItems();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	function loadItems() {
		setIsLoading(true);
		service.getAll(
			(response) => {
				if (response && response.status === 200 && response.data) {
					setItems(response.data);
				}
				setIsLoading(false);
			},
			(error) => {
				console.error(error);
				alert("Failed to load volume to mass conversions");
				setIsLoading(false);
			}
		);
	}

	function handleDelete(event, item) {
		if (event && event.preventDefault)
			event.preventDefault();

		if (!window.confirm(`Delete "${item.name}"?`))
			return;

		setDeletingId(item.volumeToMassConversionId);
		service.deleteItem(item.volumeToMassConversionId, () => {
			loadItems();
			setDeletingId(null);
		}, (error) => {
			console.error(error);
			alert("Failed to delete volume to mass conversion");
			setDeletingId(null);
		});
	}

	function handleImport() {
		try {
			const parsed = parseRelaxedJson(importText);
			const conversions = parsed.map(item => {
				return {
					name: item.Names[0],
					density: item.Density,
					Aliases: (item.Names || []).slice(1),
					notes: item.Notes || ""
				};
			});

			service.importItems(conversions, (response) => {
				if (response && response.status === 200) {
					setItems(response.data);
					setShowImport(false);
					setImportText("");
					setToastMessage("Import successful!");
					setToastOpen(true);
				} else {
					alert("Failed to import");
				}
			}, (error) => {
				console.error(error);
				alert("Failed to import: " + (error.response?.data || error.message));
			});
		} catch (e) {
			alert("Invalid JSON format. Expected array of objects with Names (array), Density (number), and optional Notes properties.");
		}
	}

	function handleExport() {
		const exportData = items.map(item => {
			const names = [item.name];
			const aliasList = normalizeAliasList(item.aliases ?? item.Aliases);
			if (aliasList.length) {
				names.push(...aliasList);
			}
			return {
				Names: names,
				Density: item.density,
				Notes: item.notes ?? ""
			};
		});

		const jsonString = JSON.stringify(exportData, null, 2);
		const blob = new Blob([jsonString], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
		link.download = `volume-to-mass-conversions-${timestamp}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setTimeout(() => URL.revokeObjectURL(url), 100);
		setToastMessage("Export successful!");
		setToastOpen(true);
	}

	if (!user.isInitialized) {
		return <AuthLoadingScreen />;
	}

	if (!hasPermission)
		return (
			<Grid container spacing={8}>
				<Grid item xs={12}>
					<NotLoggedInIcon fontSize="large" />
					<Typography variant="subtitle1">
						Sorry, you must {user.isLoggedIn ? "" : "be logged in and "}have volume to mass admin permissions to view this page!
					</Typography>
					<Typography style={{ paddingLeft: 40 }} variant="subtitle1">
						Logged in: {user.isLoggedIn ? "True" : "False"}
					</Typography>
					<Typography style={{ paddingTop: 20 }} variant="subtitle1">
						<RouterLink to="/login" >
							{user.isLoggedIn ? "" : "Login Now"}
						</RouterLink>
					</Typography>
				</Grid>
			</Grid>
		);

	return (
		<Grid container spacing={3} style={{ padding: 20 }}>
			<Grid item xs={12}>
				<Typography variant="h4" gutterBottom>
					Volume to Mass Conversions
				</Typography>
			</Grid>
			<Grid item xs={12} style={{ marginBottom: 12 }}>
				<Button
					variant="contained"
					color="primary"
					component={RouterLink}
					to="/admin/volume-to-mass-conversions/create"
				>
					<AddIcon style={{ marginRight: 10 }} />
					Add Conversion
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setShowImport(!showImport)}
					style={{ marginLeft: 12 }}
				>
					<ImportIcon style={{ marginRight: 8 }} />
					Import
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handleExport}
					style={{ marginLeft: 12 }}
				>
					<SaveAltIcon style={{ marginRight: 8 }} />
					Export
				</Button>
			</Grid>
			{showImport && (
				<Grid item xs={12}>
					<Paper style={{ padding: 20, marginBottom: 20 }}>
						<Typography variant="h6" gutterBottom>
							Import from Config Format
						</Typography>
						<Typography variant="body2" gutterBottom>
							Paste the VolumeToMassConversions JSON array from your config file:
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={8}
							variant="outlined"
							value={importText}
							onChange={(e) => setImportText(e.target.value)}
							placeholder={'[\n  { "Names": ["Flour", "All-Purpose Flour"], "Density": 0.541 },\n  ...\n]'}
							style={{ marginBottom: 10 }}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={handleImport}
							style={{ marginRight: 10 }}
						>
							Import
						</Button>
						<Button
							variant="outlined"
							onClick={() => { setShowImport(false); setImportText(""); }}
						>
							Cancel
						</Button>
					</Paper>
				</Grid>
			)}
			<Grid item xs={13}>{/* add +1 to this xs value to fix a display issue */}
				<Paper>
					{isLoading ? (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
							<Spinner size={24} style={{ marginRight: 10 }} />
							<Typography>Loading...</Typography>
						</div>
					) : (
						<Table className="nowrap-table condensed-table">
							<TableHead>
								<TableRow className="user-admin-header">
									<TableCell style={{ width: 1, padding: 4 }}></TableCell>
									<TableCell>Name</TableCell>
									<TableCell align="right">Density (g/ml)</TableCell>
									<TableCell>Aliases</TableCell>
									<TableCell>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sortedItems.map((item) => (
									<TableRow key={item.volumeToMassConversionId}>
										<TableCell align="left" style={{ width: 1, padding: 4 }}>
											<div style={{ display: "flex", gap: 4 }}>
												<Tooltip title="Edit">
													<IconButton
														color="primary"
														size="small"
														className="rb-icon-btn"
														component={RouterLink}
														to={`/admin/volume-to-mass-conversions/${item.volumeToMassConversionId}/edit`}
														aria-label="Edit volume to mass conversion"
													>
														<EditIcon fontSize="small" />
													</IconButton>
												</Tooltip>
												<Tooltip title="Delete">
													<IconButton
														color="primary"
														size="small"
														className="rb-icon-btn"
														component={RouterLink}
														to={`/admin/volume-to-mass-conversions/${item.volumeToMassConversionId}/edit`}
														onClick={(e) => handleDelete(e, item)}
														disabled={deletingId === item.volumeToMassConversionId}
														aria-label="Delete volume to mass conversion"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Tooltip>
											</div>
										</TableCell>
										<TableCell>{item.name}</TableCell>
										<TableCell align="right">{item.density}</TableCell>
										<TableCell>{formatAliasesForOverview(item.aliases)}</TableCell>
										<TableCell>{item.notes}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Paper>
			</Grid>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={toastOpen}
				autoHideDuration={3000}
				onClose={() => setToastOpen(false)}
				style={{ marginTop: 20 }}
			>
				<SnackbarContent
					style={{ backgroundColor: green[600] }}
					message={
						<div style={{ display: "flex", alignItems: "center" }}>
							<ConfirmIcon />
							<span style={{ paddingLeft: 10 }}>{toastMessage}</span>
						</div>
					}
				/>
			</Snackbar>
		</Grid>
	);
}
